import { AUTH_SERVICE_URL } from '@/config/env';

export const AUTH_EXPIRED_EVENT = 'vc-auth-expired';

export class ApiError extends Error {
  readonly status: number;

  constructor(message: string, status: number) {
    super(message);
    this.name = 'ApiError';
    this.status = status;
  }
}

/**
 * Performs an authenticated request against vc-chatbot-authentication-service.
 * Always sends the session cookie for authenticated requests.
 */
export async function apiFetch<T>(path: string, init: RequestInit = {}): Promise<T> {
  const method = (init.method ?? 'GET').toUpperCase();
  const headers = new Headers(init.headers);

  if (method !== 'GET' && method !== 'HEAD') {
    if (init.body && !headers.has('Content-Type')) {
      headers.set('Content-Type', 'application/json');
    }
  }

  const response = await fetch(`${AUTH_SERVICE_URL}${path}`, {
    ...init,
    method,
    headers,
    credentials: 'include',
  });

  if (!response.ok) {
    const error = await extractError(response);
    if (response.status === 401 && error.code === 'unauthorized' && error.message === 'Authentication required') {
      window.dispatchEvent(new Event(AUTH_EXPIRED_EVENT));
    }
    throw new ApiError(error.message, response.status);
  }

  if (response.status === 204) {
    return undefined as T;
  }

  return (await response.json()) as T;
}

async function extractError(response: Response): Promise<{ code?: string; message: string }> {
  try {
    const body = await response.json();
    if (body && typeof body.message === 'string') {
      return {
        code: typeof body.error === 'string' ? body.error : undefined,
        message: body.message,
      };
    }
    if (body && typeof body.error === 'string') return { message: body.error };
  } catch {
    // response wasn't JSON; fall through to the generic message
  }
  return { message: `Request failed with status ${response.status}` };
}

export function googleLoginUrl(): string {
  return `${AUTH_SERVICE_URL}/auth/google`;
}
